import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { useState } from "react"

import { cn } from "@/lib/utils"

const expandableTextVariants = cva("cursor-pointer block", {
  variants: {
    variant: {
      truncate: "truncate",
      "break-all": "break-all",
      "break-words": "break-words",
      "break-normal": "break-normal",
      "whitespace-nowrap": "whitespace-nowrap",
      "whitespace-pre": "whitespace-pre",
      "whitespace-pre-wrap": "whitespace-pre-wrap",
      "whitespace-pre-line": "whitespace-pre-line",
    },
    size: {
      default: "text-sm",
      sm: "text-xs",
      lg: "text-base",
      xl: "text-lg",
    },
  },
  defaultVariants: {
    variant: "truncate",
    size: "default",
  },
})

export interface ExpandableTextProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof expandableTextVariants> {
  text: string
  threshold?: number
  expandedClassName?: string
  collapsedClassName?: string
}

const ExpandableText = React.forwardRef<HTMLDivElement, ExpandableTextProps>(
  ({ className, variant, size, text, threshold = 28, expandedClassName, collapsedClassName, ...props }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false)

    // Check if text is likely to be truncated
    const isLongText = text.length > threshold

    if (!isLongText) {
      return (
        <div ref={ref} className={cn(expandableTextVariants({ variant, size, className }), "min-w-0")} {...props}>
          <span className="block">{text}</span>
        </div>
      )
    }

    return (
      <div ref={ref} className={cn("min-w-0", className)} {...props}>
        <span
          className={cn(
            "block cursor-pointer",
            expandableTextVariants({ size }),
            isExpanded
              ? expandedClassName || "whitespace-normal break-all"
              : collapsedClassName || expandableTextVariants({ variant })
          )}
          onClick={() => setIsExpanded(!isExpanded)}
          title={text}
        >
          {text}
        </span>
      </div>
    )
  }
)
ExpandableText.displayName = "ExpandableText"

export { ExpandableText }
